import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-trust-payment-digital-wallet',
  templateUrl: './trust-payment-digital-wallet.page.html',
  styleUrls: ['./trust-payment-digital-wallet.page.scss'],
})
export class TrustPaymentDigitalWalletPage implements OnInit, OnDestroy {
  @Input() caseId: string;
  @Input() responseData: any;
  @Input() debtorDetails: any;
  @Input() paymentMethod: string;
  @Input() amount: number;

  title = this.translate.instant('PAYMENT.GATEWAY.DIGITAL_WALLET_PAYMENT');
  saveBtnDisabled = false;
  isConfigured = false;
  isRedirected = false;
  paymentWindow: Window | null = null;
  private messageHandler: (event: MessageEvent) => void;

  constructor(
    private caseService: CaseService,
    private commonService: CommonService,
    private ionNav: IonNav,
    private translate: TranslateService
  ) {
    this.messageHandler = (event: MessageEvent) => {
      console.log('Received message from payment window:', event.data);
    };
  }

  /**
   * This lifecycle hook is called when the component is initialized.
   * It sets the title of the page to the payment type label.
   * It also sets the amount to the net amount of the case.
   * It reads the portal settings from local storage and
   * checks if the payment method is configured.
   * If the payment method is configured, it initializes the form.
   */
  ngOnInit() {
    this.saveBtnDisabled = false;
    const portalSettings = JSON.parse(
      localStorage.getItem('portal_settings') || '{}'
    );

    // Set the title of the page to the payment type label
    if (portalSettings?.payment_type?.trustpayment?.label) {
      this.title = portalSettings.payment_type.trustpayment.label;
    }

    // Check if the payment method is configured
    this.isConfigured = true;
    this.initiatePayment();

    // Add an event listner to the window to listen for messages
    // from the payment window
    window.addEventListener('message', this.messageHandler);
  }

  /**
   * This lifecycle hook is called when the component is destroyed.
   * It removes the event listner from the window that is used to listen
   * for messages from the payment window.
   * It also closes the payment window if it is open.
   */
  ngOnDestroy() {
    window.removeEventListener('message', this.messageHandler);
    if (this.paymentWindow && !this.paymentWindow.closed) {
      // Close the payment window if it is open
      this.paymentWindow.close();
    }
  }

  /**
   * Initiates the payment process.
   * It gets the form fields from the backend using the `getTrustPaymentDigitalWalletFormData` method.
   * It creates a form and appends the hidden fields to it.
   * It dynamically sets the action url of the form to the url returned from the backend.
   * It then appends the form to the document body and submits it.
   * If the response from the backend is not successful, it shows a toast with the message.
   */
  async initiatePayment() {
    const successUrl = window.location.href;

    const formFields = {
      amount: this.amount.toString(),
      paymentMethod: this.paymentMethod,
      gatewayId: this.responseData.id,
      successUrl: successUrl,
    };

    this.caseService
      .getTrustPaymentDigitalWalletFormData(formFields)
      .subscribe(async (res: any) => {
        if (res.result && res.data) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = res.data.form_action; // ✅ dynamically set action URL

          const hiddenFields = res.data.form_fields;

          // Loop through the hidden fields and create an input for each one
          Object.keys(hiddenFields).forEach((key) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = hiddenFields[key];
            form.appendChild(input);
          });

          if (this.paymentMethod === 'paybybank') {
            const ruleIdentifierInput = document.createElement('input');
            ruleIdentifierInput.type = 'hidden';
            ruleIdentifierInput.name = 'ruleidentifier';
            ruleIdentifierInput.value = 'STR-8';
            form.appendChild(ruleIdentifierInput);

            const successfulUrlNotificationInput = document.createElement('input');
            successfulUrlNotificationInput.type = 'hidden';
            successfulUrlNotificationInput.name = 'successfulurlnotification';
            successfulUrlNotificationInput.value = successUrl;
            form.appendChild(successfulUrlNotificationInput);
          }

          // Append the form to the document body and submit it
          document.body.appendChild(form);
          form.submit();
        } else {
          this.commonService.showToast(res.message, 'danger');
        }
      });
  }

  /**
   * @description
   * Dismisses the current page and navigates back to the previous page.
   * @returns {void}
   */
  dismiss(): void {
    this.ionNav.pop();
  }
}